import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BottomSheet } from '@tendbot/ui'

interface Props {
  isOpen: boolean
  onClose: () => void
  onCreate: (data: { title: string; context?: string; dueAt?: string; groupName?: string }) => void
  groupLabel?: string
}

export default function CreateLoopSheet({ isOpen, onClose, onCreate, groupLabel }: Props) {
  const { t } = useTranslation('followups')
  const [title, setTitle] = useState('')
  const [context, setContext] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [groupName, setGroupName] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    onCreate({
      title: title.trim(),
      context: context.trim() || undefined,
      dueAt: dueAt || undefined,
      groupName: groupName.trim() || undefined,
    })
    setTitle('')
    setContext('')
    setDueAt('')
    setGroupName('')
    onClose()
  }

  return (
    <BottomSheet
      open={isOpen}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={t('create.title')}
      showTitle
    >
      <form onSubmit={handleSubmit} className="px-5 pb-6 pt-3 space-y-4">
        <div>
          <label className="block text-pearl/60 text-xs font-medium mb-1.5">
            {t('create.titleLabel')} *
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={t('create.titlePlaceholder')}
            className="w-full px-3 py-2.5 rounded-lg bg-charcoal/50 border border-white/10 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-pearl/60 text-xs font-medium mb-1.5">
            {t('create.contextLabel')}
          </label>
          <textarea
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder={t('create.contextPlaceholder')}
            rows={3}
            className="w-full px-3 py-2.5 rounded-lg bg-charcoal/50 border border-white/10 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors resize-none"
          />
        </div>

        <div>
          <label className="block text-pearl/60 text-xs font-medium mb-1.5">
            {groupLabel ?? t('create.groupLabel')}
          </label>
          <input
            type="text"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            placeholder={t('create.groupPlaceholder')}
            className="w-full px-3 py-2.5 rounded-lg bg-charcoal/50 border border-white/10 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
          />
        </div>

        <div>
          <label className="block text-pearl/60 text-xs font-medium mb-1.5">
            {t('create.dueDateLabel')}
          </label>
          <input
            type="date"
            value={dueAt}
            onChange={e => setDueAt(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg bg-charcoal/50 border border-white/10 text-ivory text-sm focus:outline-none focus:border-gold/40 transition-colors [color-scheme:dark]"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-white/10 text-pearl/60 text-sm font-medium hover:bg-white/5 transition-colors"
          >
            {t('create.cancel')}
          </button>
          <button
            type="submit"
            disabled={!title.trim()}
            className="flex-1 py-2.5 rounded-full bg-gold text-midnight text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('create.submit')}
          </button>
        </div>
      </form>
    </BottomSheet>
  )
}
